<template>
  <BaseDialog
    :open="open"
    :max-width="'680px'"
    @close="handleClose"
  >
    <template #title>
      Aufenthalt bearbeiten
      <span
        v-if="dateDisplay"
        class="muted"
      > ({{ dateDisplay }})</span>
    </template>

    <div class="card stay-edit">
      <p class="help mt-0">
        Passen Sie Beginn und Ende des Aufenthalts an. Die Zeiten dürfen sich
        nicht mit anderen Aufenthalten überschneiden und müssen alle
        Kommen-/Gehen-Zeiten einschließen.
      </p>

      <!-- NEU: Haptische Zeitachse mit zwei Slidern -->
      <div
        v-if="hasSliderBounds"
        class="stay-slider mt-12"
      >
        <div class="stay-slider-header">
          <span class="stay-slider-label">Zeitraum des Aufenthalts</span>
          <span class="stay-slider-current">
            {{ startTime || '–' }} – {{ endTime || '–' }}
          </span>
        </div>

        <div class="stay-slider-track">
          <!-- Rail: ohne Attendance-Fenster eine durchgehende Schiene -->
          <div
            v-if="!hasInnerWindow"
            class="stay-slider-rail rail"
          />

          <!-- Mit Attendance-Fenster: zwei Rails nebeneinander
               links: Ankommen, rechts: Entlassen -->
          <div
            v-else
            class="stay-slider-rail rail stay-slider-rail-left"
            :style="railLeftStyle"
          />
          <div
            v-if="hasInnerWindow"
            class="stay-slider-rail rail stay-slider-rail-right"
            :style="railRightStyle"
          />

          <!-- Ticks für enthaltene Attendances (min / max) -->
          <div
            v-if="hasInnerWindow"
            class="stay-slider-tick"
            :style="{ left: innerLeftPct + '%' }"
          />
          <div
            v-if="hasInnerWindow"
            class="stay-slider-tick"
            :style="{ left: innerRightPct + '%' }"
          />

          <!-- Aktiver Bereich des Aufenthalts (Start–Ende) VOR dem Attendance-Fenster -->
          <div
            v-if="fillBeforeStyle"
            class="stay-slider-fill"
            :style="fillBeforeStyle"
          />

          <!-- Aktiver Bereich des Aufenthalts (Start–Ende) NACH dem Attendance-Fenster -->
          <div
            v-if="fillAfterStyle"
            class="stay-slider-fill"
            :style="fillAfterStyle"
          />

          <!-- Fallback: wenn es kein Attendance-Fenster gibt, ein durchgehender Balken -->
          <div
            v-if="!hasInnerWindow"
            class="stay-slider-fill"
            :style="fillStyle"
          />

          <!-- Slider: Beginn – liegt nur auf der linken Rail -->
          <input
            v-if="sliderStartMin !== null && sliderStartMax !== null"
            v-model.number="startMinutes"
            type="range"
            class="stay-slider-handle stay-slider-start"
            :min="sliderStartMin"
            :max="sliderStartMax"
            :step="sliderStep"
            :style="hasInnerWindow ? railLeftStyle : null"
            @input="onSliderChange('start')"
          >

          <!-- Slider: Ende – liegt nur auf der rechten Rail.
               Bei offenen Aufenthalten (ohne releaseTime) ausgeblendet. -->
          <input
            v-if="hasRelease && sliderEndMin !== null && sliderEndMax !== null"
            v-model.number="endMinutes"
            type="range"
            class="stay-slider-handle stay-slider-end"
            :min="sliderEndMin"
            :max="sliderEndMax"
            :step="sliderStep"
            :style="hasInnerWindow ? railRightStyle : null"
            @input="onSliderChange('end')"
          >
        </div>

        <div class="stay-slider-meta">
          <div class="stay-slider-bound">
            <span class="muted">frühestes Ankommen: </span>
            <span>{{ boundsTextAbsolute.min || '–' }}</span>
          </div>
          <div
            v-if="attendanceWindowText"
            class="stay-slider-bound"
          >
            <span class="muted">enthaltene Anwesenheit: </span>
            <span>{{ attendanceWindowText }}</span>
          </div>
          <div
            v-if="hasRelease"
            class="stay-slider-bound bound-release"
          >
            <span class="muted">spätestes Entlassen: </span>
            <span>{{ boundsTextAbsolute.max || '–' }}</span>
          </div>
        </div>
      </div>

      <div class="mt-12">
        <label class="field field-textarea">
          <span class="label text-small">Notiz (optional)</span>
          <textarea
            v-model.trim="noteText"
            class="input textarea"
            rows="2"
            placeholder="z. B. verspätet wegen Arzttermin"
          />
        </label>
      </div>

      <!-- NEU: Entlassungsgrund (nur sinnvoll, wenn ein released_at gesetzt ist) -->
      <div
        v-if="hasRelease"
        class="mt-12"
      >
        <label class="field">
          <span class="label text-small">Entlassungsgrund</span>
          <select
            v-model.number="releaseReasonId"
            class="input"
            :disabled="saving || reasonsLoading"
            aria-label="Entlassungsgrund auswählen"
          >
            <option :value="0">
              {{ reasonsLoading ? 'Lade…' : 'Bitte wählen' }}
            </option>
            <option
              v-for="rr in releaseReasons"
              :key="`rr-${rr.id}`"
              :value="rr.id"
            >
              {{ rr.title }}
            </option>
          </select>
          <p class="help">
            Wird nur beim Entlassen gespeichert.
          </p>
        </label>
      </div>

      <div
        class="field-grid mt-12"
        style="visibility: hidden; height: 0;"
      >
        <div class="field">
          <label for="stay-start">Beginn (Ankunft)</label>
          <input
            id="stay-start"
            v-model="startTime"
            type="time"
            step="60"
            @change="onTimeInputChange('start')"
          >
          <p
            v-if="boundsText.prev"
            class="help"
          >
            Frühestens: {{ boundsText.prev }}
          </p>
        </div>

        <div class="field">
          <label for="stay-end">Ende (Entlassung)</label>
          <input
            id="stay-end"
            v-model="endTime"
            type="time"
            step="60"
            @change="onTimeInputChange('end')"
          >
          <p
            v-if="boundsText.next"
            class="help"
          >
            Spätestens: {{ boundsText.next }}
          </p>
        </div>
      </div>

      <div
        v-if="attendanceSummary.length"
        class="attendance-box mt-12"
      >
        <div class="attendance-header">
          Enthaltene Anwesenheitszeiten
        </div>
        <ul class="attendance-list">
          <li
            v-for="(att, i) in attendanceSummary"
            :key="i"
          >
            <span class="dot" />
            <span v-if="att.label">
              {{ att.label }}
            </span>
            <span v-else>
              {{ att.from }} – {{ att.to }}
            </span>
          </li>
        </ul>
        <p
          v-if="attendanceSummary.length"
          class="help"
        >
          Der Aufenthalt muss alle aufgeführten Zeitbereiche vollständig umfassen.
        </p>
      </div>

      <p
        v-if="errorMsg"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ errorMsg }}
      </p>
    </div>

    <template #footer>
      <button
        v-if="canShowDeleteButton"
        class="btn btn-danger mr-5"
        type="button"
        :disabled="saving || deleting"
        @click="handleDeleteClick"
      >
        <span
          v-if="deleting"
          class="spinner"
          aria-hidden="true"
        />
        {{ deleteButtonLabel }}
      </button>

      <button
        class="btn"
        type="button"
        @click="handleClose"
      >
        Abbrechen
      </button>

      <button
        class="btn btn-primary"
        type="button"
        :disabled="saving"
        @click="handleSave"
      >
        <span
          v-if="saving"
          class="spinner"
          aria-hidden="true"
        />
        Speichern
      </button>
    </template>
  </BaseDialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import BaseDialog from "@/components/BaseDialog.vue";
import api from '@/services/api';

const props = defineProps({
  open: {type: Boolean, default: false},
  stay: {type: Object, default: null},
  date: {type: String, default: ''}
});

const deleting = ref(false);

const emit = defineEmits(['close', 'saved']);
const startTime = ref('');
const endTime = ref('');
const startMinutes = ref(null); // Minuten seit Tagesbeginn
const endMinutes = ref(null);
const errorMsg = ref('');
const saving = ref(false);
const noteText = ref('');

// NEU: Release-Reasons (Dropdown)
const releaseReasons = ref([]);
const reasonsLoading = ref(false);
const releaseReasonId = ref(0);

// Hat der aktuelle Stay eine Entlassungszeit?
const hasRelease = computed(() => {
  const rt = props.stay?.releaseTime;
  return rt instanceof Date && !Number.isNaN(rt.getTime());
});

const dateDisplay = computed(() => {
  if (!props.date) return '';
  const d = new Date(props.date);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(d);
});

watch(
    () => props.open,
    (val) => {
      if (val) {
        initFromStay();
        loadReleaseReasonsIfNeeded();
      } else {
        resetState();
      }
    }
);

watch(
    () => props.stay,
    () => {
      if (props.open) {
        initFromStay();
        loadReleaseReasonsIfNeeded();
      }
    }
);


function formatTimeForInput(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function formatTimeForText(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(d);
}

function minutesOfDay(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
}

function minutesToTimeString(mins) {
  if (!Number.isFinite(mins)) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function parseTimeToDate(baseDateStr, timeStr) {
  if (!baseDateStr || !timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const d = new Date(baseDateStr);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(h, m, 0, 0);
  return d;
}

function resetState() {
  startTime.value = '';
  endTime.value = '';
  startMinutes.value = null;
  endMinutes.value = null;
  noteText.value = '';
  errorMsg.value = '';
  saving.value = false;

  // NEU
  releaseReasonId.value = 0;
}

function initFromStay() {
  errorMsg.value = '';
  const s = props.stay;
  if (!s) {
    resetState();
    return;
  }

  startTime.value = formatTimeForInput(s.arrivalTime);
  endTime.value = formatTimeForInput(s.releaseTime);
  startMinutes.value = minutesOfDay(s.arrivalTime);
  endMinutes.value = minutesOfDay(s.releaseTime);

  // NEU: Robustheit – falls Daten inkonsistent sind, Endzeit minimal rechts von Start halten
  if (
      hasRelease.value
      && Number.isFinite(startMinutes.value)
      && Number.isFinite(endMinutes.value)
      && endMinutes.value <= startMinutes.value
  ) {
    endMinutes.value = startMinutes.value + sliderStep;
    endTime.value = minutesToTimeString(endMinutes.value);
  }

  // Note aus Stay bzw. (Timeline-)Release-Kind übernehmen
  const rawNote =
      s.note ??
      (s.release && s.release.note) ??
      '';

  noteText.value = rawNote ? String(rawNote) : '';

  // NEU: falls Stay bereits eine ID liefert, vorselektieren (best effort)
  const rr = s.releaseReasonId ?? null;

  const rrNum = Number(rr);
  releaseReasonId.value = Number.isFinite(rrNum) && rrNum > 0 ? rrNum : 0;
}

async function loadReleaseReasonsIfNeeded() {
  if (reasonsLoading.value) return;
  if (Array.isArray(releaseReasons.value) && releaseReasons.value.length) return;

  reasonsLoading.value = true;
  try {
    const {data} = await api.get('/attendance/reasons');
    const raw = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
    releaseReasons.value = raw.map(it => ({
      id: Number(it?.id),
      title: String(it?.title ?? '')
    })).filter(it => Number.isFinite(it.id) && it.id > 0 && it.title.trim().length > 0);
  } catch {
    releaseReasons.value = [];
  } finally {
    reasonsLoading.value = false;
  }
}

const boundsText = computed(() => {
  const prev = props.stay?.prevStay;
  const next = props.stay?.nextStay;
  const prevLimit = prev?.releaseTime ? formatTimeForText(prev.releaseTime) : '';
  const nextLimit = next?.arrivalTime ? formatTimeForText(next.arrivalTime) : '';
  return {
    prev: prevLimit ? `nach ${prevLimit}` : '',
    next: nextLimit ? `vor ${nextLimit}` : ''
  };
});

const attendanceSummary = computed(() => {
  const list = [];
  const s = props.stay;
  if (!s || !Array.isArray(s.attendances)) return list;

  for (const att of s.attendances) {
    const from = att.checkin ? formatTimeForText(att.checkin) : '';
    const to = att.checkout ? formatTimeForText(att.checkout) : '';
    if (from || to) {
      list.push({
        from,
        to,
        label: from && to ? `${from} – ${to}` : from || to
      });
    }
  }
  return list;
});

const hasAttendances = computed(() => attendanceSummary.value.length > 0);

const canDeleteStay = computed(() => {
  return Boolean(props.stay?.id) && !hasAttendances.value;
});

const canDeleteRelease = computed(() => {
  return Boolean(props.stay?.id) && hasAttendances.value && hasRelease.value;
});

const canShowDeleteButton = computed(() => canDeleteStay.value || canDeleteRelease.value);

const deleteButtonLabel = computed(() => {
  return canDeleteRelease.value ? 'Entlassen löschen' : 'Aufenthalt löschen';
});

async function handleDeleteClick() {
  if (canDeleteRelease.value) {
    await handleDeleteRelease();
    return;
  }
  await handleDeleteStay();
}

async function handleDeleteStay() {
  if (deleting.value || saving.value) return;

  if (!props.stay?.id) {
    errorMsg.value = 'Ungültige Auswahl: Stay-ID fehlt.';
    return;
  }

  const ok = window.confirm('Aufenthalt wirklich löschen? Dies kann nicht rückgängig gemacht werden.');
  if (!ok) return;

  deleting.value = true;
  errorMsg.value = '';
  try {
    await api.delete(`/presence/stays/${props.stay.id}`);

    emit('saved', {
      stayId: props.stay.id,
      deleted: true
    });

    emit('close');
  } catch (e) {
    let msg = 'Löschen fehlgeschlagen.';
    const backendErr = e?.response?.data?.error;
    if (backendErr) {
      if (typeof backendErr === 'string') {
        msg = backendErr;
      } else if (backendErr.message) {
        msg = backendErr.message;
      } else {
        try {
          msg = JSON.stringify(backendErr);
        } catch {
          msg = 'Löschen fehlgeschlagen (Unbekannter Fehler).';
        }
      }
    } else if (e?.message) {
      msg = e.message;
    }
    errorMsg.value = msg;
  } finally {
    deleting.value = false;
  }
}

async function handleDeleteRelease() {
  if (deleting.value || saving.value) return;

  if (!props.stay?.id) {
    errorMsg.value = 'Ungültige Auswahl: Stay-ID fehlt.';
    return;
  }

  const ok = window.confirm('Entlassung wirklich entfernen? Der Aufenthalt bleibt bestehen.');
  if (!ok) return;

  deleting.value = true;
  errorMsg.value = '';
  try {
    const arrived = props.stay?.arrivalTime instanceof Date ? props.stay.arrivalTime : null;
    if (!arrived || Number.isNaN(arrived.getTime())) {
      errorMsg.value = 'Entlassung kann nicht entfernt werden: Ankunftszeit fehlt.';
      return;
    }

    const payload = {
      id: props.stay.id,
      arrived_at: arrived.toISOString(),
      released_at: null
    };

    const {data} = await api.patch('/presence/stays', payload);
    const updatedStay = data?.stay ?? null;

    emit('saved', {
      stayId: props.stay.id,
      releaseDeleted: true,
      updatedStay
    });

    emit('close');
  } catch (e) {
    let msg = 'Entlassen löschen fehlgeschlagen.';
    const backendErr = e?.response?.data?.error;
    if (backendErr) {
      if (typeof backendErr === 'string') {
        msg = backendErr;
      } else if (backendErr.message) {
        msg = backendErr.message;
      } else {
        try {
          msg = JSON.stringify(backendErr);
        } catch {
          msg = 'Entlassen löschen fehlgeschlagen (Unbekannter Fehler).';
        }
      }
    } else if (e?.message) {
      msg = e.message;
    }
    errorMsg.value = msg;
  } finally {
    deleting.value = false;
  }
}

// Slider-Bounds aus angrenzenden Stays und Attendances
const sliderStep = 1; // Minuten-Schrittweite

const attendanceWindow = computed(() => {
  const s = props.stay;
  if (!s || !Array.isArray(s.attendances) || !s.attendances.length) {
    return {min: null, max: null};
  }
  let min = null;
  let max = null;

  for (const att of s.attendances) {
    const cIn = att.checkin ? minutesOfDay(att.checkin) : null;
    const cOut = att.checkout ? minutesOfDay(att.checkout) : null;
    const values = [cIn, cOut].filter(Number.isFinite);
    if (!values.length) continue;
    const aMin = Math.min(...values);
    const aMax = Math.max(...values);
    min = min === null ? aMin : Math.min(min, aMin);
    max = max === null ? aMax : Math.max(max, aMax);
  }
  return {min, max};
});

// NEU: Effektives Attendance-Fenster erzwingen (auch bei min===max → 1-Minuten-Barriere)
const effectiveAttendanceWindow = computed(() => {
  const w = attendanceWindow.value;
  if (!Number.isFinite(w.min) || !Number.isFinite(w.max)) {
    return {min: null, max: null};
  }

  let min = w.min;
  let max = w.max;

  if (min === max) {
    // Barriere erzeugen, damit Start/Ende nicht „durchrutschen“
    // Innerhalb der globalen Bounds bleiben
    const upper = sliderGlobalMax.value;
    const lower = sliderGlobalMin.value;

    // bevorzugt nach rechts aufspannen, sonst nach links
    if (min + sliderStep <= upper) {
      max = min + sliderStep;
    } else if (min - sliderStep >= lower) {
      min = min - sliderStep;
      max = min + sliderStep;
    } else {
      // Extrem-Fallback (sollte praktisch nie passieren)
      max = min + 0;
    }
  }

  return {min, max};
});

const hasInnerWindow = computed(() => {
  const w = effectiveAttendanceWindow.value;
  return Number.isFinite(w.min) && Number.isFinite(w.max) && w.min <= w.max;
});

const sliderOuterBounds = computed(() => {
  const s = props.stay;
  if (!s) return {min: null, max: null};

  // Fallbacks: 06:00 bis 20:00
  const fallbackMin = 6 * 60;
  const fallbackMax = 20 * 60;

  let min = fallbackMin;
  let max = fallbackMax;

  if (s.prevStay?.releaseTime) {
    const m = minutesOfDay(s.prevStay.releaseTime);
    if (Number.isFinite(m)) min = m;
  }
  if (s.nextStay?.arrivalTime) {
    const m = minutesOfDay(s.nextStay.arrivalTime);
    if (Number.isFinite(m)) max = m;
  }

  if (min >= max) {
    // Notfalls Tagesgrenzen
    min = fallbackMin;
    max = fallbackMax;
  }

  return {min, max};
});

const hasSliderBounds = computed(() => {
  const o = sliderOuterBounds.value;
  return Number.isFinite(o.min) && Number.isFinite(o.max);
});

const sliderGlobalMin = computed(() => sliderOuterBounds.value.min ?? 0);
const sliderGlobalMax = computed(() => sliderOuterBounds.value.max ?? 24 * 60);

// Start darf nur links vom inneren Min (Attendance-Min) liegen
const sliderStartMin = computed(() => sliderGlobalMin.value);
const sliderStartMax = computed(() => {
  const inner = effectiveAttendanceWindow.value;
  if (Number.isFinite(inner.min)) {
    return Math.min(inner.min, sliderGlobalMax.value);
  }
  return sliderGlobalMax.value;
});

// Ende darf nur rechts vom inneren Max (Attendance-Max) liegen
const sliderEndMax = computed(() => sliderGlobalMax.value);
const sliderEndMin = computed(() => {
  const inner = effectiveAttendanceWindow.value;
  if (Number.isFinite(inner.max)) {
    return Math.max(inner.max, sliderGlobalMin.value);
  }
  return sliderGlobalMin.value;
});

const innerLeftPct = computed(() => {
  if (!hasInnerWindow.value) return 0;
  const w = effectiveAttendanceWindow.value;
  const total = sliderGlobalMax.value - sliderGlobalMin.value;
  if (total <= 0) return 0;
  return ((w.min - sliderGlobalMin.value) / total) * 100;
});

const innerRightPct = computed(() => {
  if (!hasInnerWindow.value) return 0;
  const w = effectiveAttendanceWindow.value;
  const total = sliderGlobalMax.value - sliderGlobalMin.value;
  if (total <= 0) return 0;
  return ((w.max - sliderGlobalMin.value) / total) * 100;
});

const boundsTextAbsolute = computed(() => {
  const o = sliderOuterBounds.value;
  const minTxt = Number.isFinite(o.min) ? minutesToTimeString(o.min) : '';
  const maxTxt = Number.isFinite(o.max) ? minutesToTimeString(o.max) : '';
  return {min: minTxt, max: maxTxt};
});

const attendanceWindowText = computed(() => {
  const w = effectiveAttendanceWindow.value;
  if (!Number.isFinite(w.min) || !Number.isFinite(w.max)) return '';
  return `${minutesToTimeString(w.min)} – ${minutesToTimeString(w.max)}`;
});

// Visuelle Aufteilung in zwei Rails (links: Ankommen, rechts: Entlassen)
const railLeftStyle = computed(() => {
  if (!hasInnerWindow.value) {
    return {left: '0%', width: '100%'};
  }
  const left = 0;
  const right = Math.max(Math.min(innerLeftPct.value, 100), 0);
  const width = Math.max(right - left, 0);
  return {
    left: `${left}%`,
    width: `${width}%`
  };
});

const railRightStyle = computed(() => {
  if (!hasInnerWindow.value) {
    return {left: '0%', width: '100%'};
  }
  const left = Math.max(Math.min(innerRightPct.value, 100), 0);
  const width = Math.max(100 - left, 0);
  return {
    left: `${left}%`,
    width: `${width}%`
  };
});

// Gesamter Füllbereich (Fallback, wenn es kein Attendance-Fenster gibt)
const fillStyle = computed(() => {
  if (!Number.isFinite(startMinutes.value) || !Number.isFinite(endMinutes.value)) {
    return {left: '0%', width: '0%'};
  }
  const total = sliderGlobalMax.value - sliderGlobalMin.value;
  if (total <= 0) return {left: '0%', width: '0%'};

  const s = Math.max(sliderGlobalMin.value, Math.min(startMinutes.value, sliderGlobalMax.value));
  const e = Math.max(sliderGlobalMin.value, Math.min(endMinutes.value, sliderGlobalMax.value));
  const leftPct = ((Math.min(s, e) - sliderGlobalMin.value) / total) * 100;
  const rightPct = ((Math.max(s, e) - sliderGlobalMin.value) / total) * 100;
  const widthPct = Math.max(rightPct - leftPct, 0);

  return {
    left: `${leftPct}%`,
    width: `${widthPct}%`
  };
});

// Visuelle Aufteilung der Rail in zwei Segmente (links: Ankunft, rechts: Entlassung)

// Segment vor dem Attendance-Fenster (Start → inner.min)
const fillBeforeStyle = computed(() => {
  if (!hasInnerWindow.value ||
      !Number.isFinite(startMinutes.value) ||
      !Number.isFinite(endMinutes.value)) {
    return null;
  }
  const w = attendanceWindow.value;
  const total = sliderGlobalMax.value - sliderGlobalMin.value;
  if (total <= 0) return null;

  const segmentEnd = Math.min(w.min, endMinutes.value);
  const segmentStart = Math.min(startMinutes.value, segmentEnd);

  if (segmentEnd <= segmentStart) return null;

  const leftPct = ((segmentStart - sliderGlobalMin.value) / total) * 100;
  const rightPct = ((segmentEnd - sliderGlobalMin.value) / total) * 100;
  const widthPct = Math.max(rightPct - leftPct, 0);

  return {
    left: `${leftPct}%`,
    width: `${widthPct}%`
  };
});

// Segment nach dem Attendance-Fenster (inner.max → End)
const fillAfterStyle = computed(() => {
  if (!hasInnerWindow.value ||
      !Number.isFinite(startMinutes.value) ||
      !Number.isFinite(endMinutes.value)) {
    return null;
  }
  const w = attendanceWindow.value;
  const total = sliderGlobalMax.value - sliderGlobalMin.value;
  if (total <= 0) return null;

  const segmentStart = Math.max(w.max, startMinutes.value);
  const segmentEnd = Math.max(endMinutes.value, segmentStart);

  if (segmentEnd <= segmentStart) return null;

  const leftPct = ((segmentStart - sliderGlobalMin.value) / total) * 100;
  const rightPct = ((segmentEnd - sliderGlobalMin.value) / total) * 100;
  const widthPct = Math.max(rightPct - leftPct, 0);

  return {
    left: `${leftPct}%`,
    width: `${widthPct}%`
  };
});

// Tick-Positionen werden über innerLeftPct/innerRightPct bereitgestellt (kein innerTickStyles mehr)

function syncMinutesFromTime(which) {
  if (!props.date) return;
  const inner = effectiveAttendanceWindow.value;

  if (which === 'start') {
    const d = parseTimeToDate(props.date, startTime.value);
    startMinutes.value = minutesOfDay(d);
    // Start nie rechts vom linken Tick
    if (Number.isFinite(inner.min) && startMinutes.value > inner.min) {
      startMinutes.value = inner.min;
      startTime.value = minutesToTimeString(startMinutes.value);
    }
  } else if (which === 'end') {
    const d = parseTimeToDate(props.date, endTime.value);
    endMinutes.value = minutesOfDay(d);
    // Ende nie links vom rechten Tick
    if (Number.isFinite(inner.max) && endMinutes.value < inner.max) {
      endMinutes.value = inner.max;
      endTime.value = minutesToTimeString(endMinutes.value);
    }
  }
}

function syncTimeFromMinutes(which) {
  if (!props.date) return;
  if (which === 'start' && Number.isFinite(startMinutes.value)) {
    startTime.value = minutesToTimeString(startMinutes.value);
  } else if (which === 'end' && Number.isFinite(endMinutes.value)) {
    endTime.value = minutesToTimeString(endMinutes.value);
  }
}

function onSliderChange(which) {
  const inner = effectiveAttendanceWindow.value;

  if (which === 'start') {
    if (Number.isFinite(inner.min) && startMinutes.value > inner.min) {
      startMinutes.value = inner.min;
    }
    if (Number.isFinite(endMinutes.value) && startMinutes.value >= endMinutes.value) {
      startMinutes.value = endMinutes.value - sliderStep;
    }
    syncTimeFromMinutes('start');
  } else if (which === 'end') {
    if (Number.isFinite(inner.max) && endMinutes.value < inner.max) {
      endMinutes.value = inner.max;
    }
    if (Number.isFinite(startMinutes.value) && endMinutes.value <= startMinutes.value) {
      endMinutes.value = startMinutes.value + sliderStep;
    }
    syncTimeFromMinutes('end');
  }
  errorMsg.value = '';
}

function onTimeInputChange(which) {
  syncMinutesFromTime(which);
  errorMsg.value = '';
}

function validate() {
  errorMsg.value = '';

  const hasReleaseStay = hasRelease.value;

  // Für offene Stays: nur Beginn erforderlich.
  if (!startTime.value || (hasReleaseStay && !endTime.value)) {
    errorMsg.value = hasReleaseStay
        ? 'Bitte sowohl Beginn- als auch Endzeit angeben.'
        : 'Bitte den Beginn des Aufenthalts angeben.';
    return false;
  }

  const start = parseTimeToDate(props.date, startTime.value);
  const end = hasReleaseStay && endTime.value
      ? parseTimeToDate(props.date, endTime.value)
      : null;

  if (!start || (hasReleaseStay && endTime.value && !end)) {
    errorMsg.value = 'Die angegebenen Zeiten sind ungültig.';
    return false;
  }

  if (hasReleaseStay && end && end <= start) {
    errorMsg.value = 'Die Endzeit muss nach der Beginnzeit liegen.';
    return false;
  }

  const s = props.stay;
  if (!s) return false;

  // 1) Grenzen zu vorherigem/nächstem Stay
  if (s.prevStay && s.prevStay.releaseTime) {
    if (start <= s.prevStay.releaseTime) {
      errorMsg.value = `Der Beginn muss nach dem Ende des vorherigen Aufenthalts (${formatTimeForText(s.prevStay.releaseTime)}) liegen.`;
      return false;
    }
  }

  if (hasReleaseStay && end && s.nextStay && s.nextStay.arrivalTime) {
    if (end >= s.nextStay.arrivalTime) {
      errorMsg.value = `Das Ende muss vor dem Beginn des nächsten Aufenthalts (${formatTimeForText(s.nextStay.arrivalTime)}) liegen.`;
      return false;
    }
  }

  // 2) Alle Attendances müssen vollständig umschlossen sein
  if (Array.isArray(s.attendances)) {
    for (const att of s.attendances) {
      const cIn = att.checkin;
      const cOut = att.checkout;
      const min = cIn && cOut ? (cIn < cOut ? cIn : cOut) : (cIn || cOut);
      const max = cIn && cOut ? (cIn > cOut ? cIn : cOut) : (cIn || cOut);
      if (!min || !max) continue;

      // Offene Stays: nur sicherstellen, dass der Beginn nicht NACH dem frühesten Attendance-Punkt liegt.
      // Geschlossene Stays: zusätzlich sicherstellen, dass das Ende nicht VOR dem spätesten Attendance-Punkt liegt.
      if (start > min || (hasReleaseStay && end && end < max)) {
        errorMsg.value = `Der Aufenthalt muss die Anwesenheitszeit von ${formatTimeForText(min)} bis ${formatTimeForText(max)} vollständig enthalten.`;
        return false;
      }
    }
  }

  return {start, end};
}

async function handleSave() {
  if (saving.value) return;
  const result = validate();
  if (!result) return;

  if (!props.stay?.id) {
    errorMsg.value = 'Ungültige Auswahl: Stay-ID fehlt.';
    return;
  }

  saving.value = true;
  errorMsg.value = '';
  try {
    const payload = {
      id: props.stay.id,
      arrived_at: result.start.toISOString(),
      note: noteText.value || null
    };

    if (result.end instanceof Date && !Number.isNaN(result.end.getTime())) {
      payload.released_at = result.end.toISOString();

      const rid = Number(releaseReasonId.value);
      if (Number.isFinite(rid) && rid > 0) {
        payload.release_reason_id = rid;
        payload.releaseReasonId = rid;
      }
    } else {
      // offener Aufenthalt → Endzeit explizit null
      payload.released_at = null;
      payload.release_reason_id = null;
      payload.releaseReasonId = null;
    }

    const {data} = await api.patch('/presence/stays', payload);
    const updatedStay = data?.stay ?? null;

    emit('saved', {
      stayId: props.stay.id,
      arrivalIndex: props.stay?.arrivalIndex,
      releaseIndex: props.stay?.releaseIndex,
      newStart: result.start,
      newEnd: result.end ?? null,
      updatedStay
    });
  } catch (e) {
    let msg = 'Speichern fehlgeschlagen.';
    // Axios-Fehlerformat berücksichtigen
    // eslint-disable-next-line no-unsafe-optional-chaining
    const backendErr = e?.response?.data?.error;
    if (backendErr) {
      if (typeof backendErr === 'string') {
        msg = backendErr;
      } else if (backendErr.message) {
        msg = backendErr.message;
      } else {
        try {
          msg = JSON.stringify(backendErr);
        } catch {
          msg = 'Speichern fehlgeschlagen (Unbekannter Fehler).';
        }
      }
    } else if (e?.message) {
      msg = e.message;
    }
    errorMsg.value = msg;
    return;
  } finally {
    saving.value = false;
  }
}

function handleClose() {
  emit('close');
}
</script>

<style scoped>
.stay-edit {
  padding: 0;
  border: none;
  box-shadow: none;
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
}

.mt-12 {
  margin-top: 12px;
}

.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.field label {
  display: block;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 4px;
}

.field input[type="time"] {
  width: 100%;
  height: 38px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
}

.field input[type="time"]:focus-visible {
  outline: 3px solid var(--focus);
  border-color: var(--primary);
}

.attendance-box {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--surface);
}

.stay-slider {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--surface);
  box-shadow: var(--shadow-1);
}

.stay-slider-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}

.stay-slider-label {
  font-size: 13px;
  font-weight: 500;
}

.stay-slider-current {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary);
}

.stay-slider-track {
  position: relative;
  height: 32px;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.stay-slider-rail {
  position: absolute;
  height: 6px;
  border-radius: 999px;
  background: var(--bg);
  border: 1px solid var(--border);
  left: 0;
  width: 100%;
}

/* Variante für die sichtbaren Rails (Ankunft/Entlassung) */
.stay-slider-rail.rail {
  z-index: 1;
}

/* Füllsegmente (außerhalb des Attendance-Fensters) */
.stay-slider-fill {
  position: absolute;
  height: 6px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--primary) 40%, transparent);
  z-index: 2;
}

.stay-slider-handle {
  -webkit-appearance: none;
  appearance: none;
  position: absolute;
  width: 100%; /* wird durch :style width überschrieben, wenn hasInnerWindow */
  background: transparent;
  pointer-events: none;
  z-index: 4;
}

.stay-slider-handle::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  pointer-events: auto;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid #fff;
  background: var(--primary);
  box-shadow: 0 2px 6px rgba(15, 23, 42, .2);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.stay-slider-handle::-moz-range-thumb {
  pointer-events: auto;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid #fff;
  background: var(--primary);
  box-shadow: 0 2px 6px rgba(15, 23, 42, .2);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.stay-slider-handle::-webkit-slider-thumb:hover,
.stay-slider-handle::-moz-range-thumb:hover {
  transform: scale(1.05);
  box-shadow: 0 3px 8px rgba(15, 23, 42, .25);
}

.stay-slider-start::-webkit-slider-thumb,
.stay-slider-start::-moz-range-thumb {
  background: var(--success);
}

.stay-slider-end::-webkit-slider-thumb,
.stay-slider-end::-moz-range-thumb {
  background: var(--warn);
}

.stay-slider-handle::-webkit-slider-runnable-track,
.stay-slider-handle::-moz-range-track {
  background: transparent;
}

.stay-slider-meta {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px 30px;
  font-size: 12px;
  line-height: 16px;
}

.stay-slider-bound.bound-release {
  margin-left: auto;
}

.stay-slider-bound span:last-child {
  font-weight: 500;
}

.attendance-header {
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
}

.attendance-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.attendance-list li {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 18px;
}

.attendance-list .dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--primary);
  flex-shrink: 0;
}

:deep(.dialog-footer) {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

/* NEU: Danger-Button-Variante auf bestehenden Tokens */
.btn-danger {
  background: var(--danger);
  color: #fff;
  border: 1px solid var(--danger);
}

.btn-danger:hover {
  background: color-mix(in srgb, var(--danger) 90%, #000);
}

.notice {
  border-radius: 6px;
}

@media (max-width: 1199px) {
  .stay-slider-handle::-moz-range-thumb {
    width: 28px;
    height: 28px;
  }
}
</style>
